/**
 * FitPlus Platform AI chat client (anonymous session UUIDs, no ID token).
 */

const DEFAULT_BASE = 'http://localhost:9050';
const SESSION_LIST_KEY = 'fitplusAiChatSessions';

function apiBase() {
  return (process.env.VUE_APP_FITPLUS_PLATFORM_API_BASE || DEFAULT_BASE).replace(/\/$/, '');
}

function buildUrl(path) {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${apiBase()}${normalized}`;
}

async function requestJson(path, init) {
  const res = await fetch(buildUrl(path), {
    ...init,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...((init && init.headers) || {}),
    },
  });

  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body && body.detail) {
        detail = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
      }
    } catch (e) {
      /* ignore */
    }
    throw new Error(`AI-Chat API: ${detail}`);
  }

  return res.json();
}

export function createAiChatSession(locale = 'de') {
  return requestJson('/api/v1/chat/sessions', {
    method: 'POST',
    body: JSON.stringify({ locale }),
  });
}

export function getAiChatSession(sessionId) {
  return requestJson(`/api/v1/chat/sessions/${encodeURIComponent(sessionId)}`);
}

/**
 * @param {string} sessionId
 * @param {string} message
 * @param {{ userId?: string|null }} [options]
 */
export function sendAiChatMessage(sessionId, message, options) {
  const userId = options && options.userId ? options.userId : null;
  return requestJson(`/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/messages`, {
    method: 'POST',
    body: JSON.stringify({
      message,
      ...(userId ? { user_id: userId } : {}),
    }),
  });
}

export function resolveAiChatSessionTitle(options) {
  const opts = options || {};
  const fromMessage = (opts.sessionTitle || '').trim();
  if (fromMessage) return fromMessage;

  const fromSession = (opts.sessionResponseTitle || '').trim();
  if (fromSession) return fromSession;

  const items = opts.referencedItems || [];
  if (items.length === 1 && items[0] && items[0].title) {
    return String(items[0].title).trim();
  }

  const userText = (opts.userMessage || '').trim().replace(/\s+/g, ' ');
  if (!userText) return undefined;
  return userText.length > 42 ? `${userText.slice(0, 42)}…` : userText;
}

/** Local comfort list of recent session ids (backend remains source of truth). */
export function listLocalAiChatSessions() {
  try {
    const raw = window.localStorage.getItem(SESSION_LIST_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

export function rememberLocalAiChatSession(session) {
  const list = listLocalAiChatSessions().filter((s) => s.sessionId !== session.sessionId);
  list.unshift({
    sessionId: session.sessionId,
    title: session.title || 'Neuer Chat',
    lastMessagePreview: session.lastMessagePreview || '',
    updatedAt: session.updatedAt || new Date().toISOString(),
  });
  window.localStorage.setItem(SESSION_LIST_KEY, JSON.stringify(list.slice(0, 40)));
}

export function removeLocalAiChatSession(sessionId) {
  const list = listLocalAiChatSessions().filter((s) => s.sessionId !== sessionId);
  window.localStorage.setItem(SESSION_LIST_KEY, JSON.stringify(list));
}

/**
 * Map a content reference to an in-app route.
 * @param {{ canonical_id?: string, type?: string, detail_url?: string }} ref
 */
export function routeForContentReference(ref) {
  if (!ref) return null;
  if (ref.detail_url && ref.detail_url.startsWith('/')) {
    return ref.detail_url;
  }
  const id = String(ref.canonical_id || '');
  const match = id.match(/^(workout|article|playlist):(\d+)$/i);
  if (!match) return null;
  const type = match[1].toLowerCase();
  const pageId = match[2];
  if (type === 'workout') return `/workout/${pageId}`;
  if (type === 'playlist') return `/playlist/start?playlist_id=${pageId}`;
  if (type === 'article') return `/articles/${pageId}`;
  return null;
}
